// SetDetailPage: edits the Set_Draft (Members, Coverage_Floor, Summary_Rules), publishes it, compares versions, lists
// its runs, and starts runs (blueprint-sets Req 1.4, 2.8, 3.1-3.3, 3.6, 4.10, 10.8).
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Link from '@cloudscape-design/components/link';
import Modal from '@cloudscape-design/components/modal';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Table from '@cloudscape-design/components/table';
import Textarea from '@cloudscape-design/components/textarea';
import { validateSummaryRules } from '@bda/shared';
import { api, ApiError } from '../../api/client';
import { keys, useBlueprints, useRun, useSet } from '../../api/hooks';
import { SetMembersEditor } from './SetMembersEditor';
import { SummaryRulesEditor, type SummaryRulesMember } from './SummaryRulesEditor';
import { formatDate } from '../../lib/format';
import { ErrorDetail, errorMessage, useNotify } from '../../lib/notify';
import { COVERAGE_FLOOR_LABEL, CoverageFloorInput, shareToPercent } from './coverageFloor';
import { DetailsFields, RouterLink, SetRunsTable, SetVersionDiff, useMemberSchemas } from './setShared';
import { fieldErrorsOf, stateOf, useSetForm } from './setForm';

// ---------- SetDetailPage (Req 1.4, 2.8, 3.1-3.3, 3.6, 4.10, 10.8) ----------

export function SetDetailPage() {
  const { id } = useParams();
  const q = useSet(id);
  if (q.error)
    return (
      <Alert type="error">
        <ErrorDetail error={q.error} />
      </Alert>
    );
  if (!q.data || !id) return <Spinner size="large" />;
  return <SetEditor key={id} id={id} detail={q.data} />;
}

export type SetDetail = Awaited<ReturnType<typeof api.getSet>>;

function SetEditor({ id, detail }: { id: string; detail: SetDetail }) {
  const navigate = useNavigate();
  const notify = useNotify();
  const qc = useQueryClient();
  const set = detail.set;
  const [basis] = useState(() => stateOf(set));
  const f = useSetForm(id, basis, set.revision);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState<number | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [changeNote, setChangeNote] = useState('');
  const [publishing, setPublishing] = useState(false);
  /** Blueprint ids the last rejected publish named as still at their draft (`details.draftMembers`, Req 3.2). */
  const [draftMembers, setDraftMembers] = useState<string[]>([]);
  const [forPublish, setForPublish] = useState(false);
  const [selectedVersions, setSelectedVersions] = useState<SetDetail['versions']>([]);
  const [compare, setCompare] = useState<[number, number] | null>(null);

  const blueprints = useBlueprints({ archived: 'false' });
  const byId = useMemo(() => new Map((blueprints.data?.items ?? []).map((b) => [b.id, b])), [blueprints.data]);
  const nameOf = (blueprintId: string) =>
    byId.get(blueprintId)?.name ??
    detail.members.find((m) => m.blueprintId === blueprintId)?.name ??
    'Unknown blueprint';
  const schemas = useMemberSchemas(f.state.members, detail.members);
  const rulesMembers: SummaryRulesMember[] = f.state.members.map((m) => ({
    blueprintId: m.blueprintId,
    name: nameOf(m.blueprintId),
    schema: schemas.schemaOf(m) ?? null,
  }));
  const ruleErrors = schemas.pending
    ? {}
    : validateSummaryRules(
        f.state.summaryRules,
        rulesMembers.flatMap((m) => (m.schema ? [{ blueprintId: m.blueprintId, schema: m.schema }] : [])),
      );
  const blocking = f.blocking || Object.keys(ruleErrors).length > 0;

  // "Run on test document" (Req 10.8): the Sample_Packet the set was created from, while it is kept.
  const testRun = useRun(set.testRunId ?? undefined);

  async function save(overwriteRevision?: number): Promise<boolean> {
    f.setShowErrors(true);
    if (blocking || 'error' in f.floor) {
      notify.error('Fix the highlighted problems before saving.');
      return false;
    }
    setSaving(true);
    try {
      const s = await api.updateSet(id, {
        name: f.state.name,
        description: f.state.description,
        members: f.state.members,
        summaryRules: f.state.summaryRules,
        coverageFloor: f.floor.share,
        expectedRevision: overwriteRevision ?? f.revision ?? set.revision,
      });
      f.markSaved(s);
      setConflict(null);
      await qc.invalidateQueries({ queryKey: keys.set(id) });
      await qc.invalidateQueries({ queryKey: ['sets'] });
      notify.success(`Saved "${s.name}".`);
      return true;
    } catch (e) {
      const fe = fieldErrorsOf(e);
      if (fe) {
        f.applyApiErrors(fe);
        notify.error('Fix the highlighted problems before saving.');
      } else if (e instanceof ApiError && e.code === 'CONFLICT') {
        setConflict(Number(e.details?.currentRevision ?? 0) || null);
        notify.error(e.message);
      } else notify.error(errorMessage(e), e);
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function publish() {
    setPublishing(true);
    try {
      const r = await api.publishSet(id, changeNote);
      setPublishOpen(false);
      setChangeNote('');
      setDraftMembers([]);
      setForPublish(false);
      await qc.invalidateQueries({ queryKey: keys.set(id) });
      await qc.invalidateQueries({ queryKey: ['sets'] });
      notify.success(`Published version ${r.version.version}.`);
    } catch (e) {
      const fe = fieldErrorsOf(e);
      setPublishOpen(false);
      if (fe) {
        f.applyApiErrors(fe);
        const drafts = (e as ApiError).details?.draftMembers;
        setDraftMembers(Array.isArray(drafts) ? drafts.filter((x): x is string => typeof x === 'string') : []);
        setForPublish(true);
        notify.error('The set cannot be published yet. Fix the problems shown on the page.');
      } else notify.error(errorMessage(e), e, 'Publish set');
    } finally {
      setPublishing(false);
    }
  }

  async function runOnTestDocument() {
    if (!set.testRunId) return;
    if (f.dirty && !(await save())) return;
    try {
      const run = await api.retryRun(set.testRunId, { setId: id, setVersion: 'DRAFT' });
      navigate(`/runs/${run.id}`);
    } catch (e) {
      notify.error(errorMessage(e), e);
    }
  }

  const pinMember = (blueprintId: string, version: number) =>
    f.update(
      'members',
      f.state.members.map((m) => (m.blueprintId === blueprintId && m.ref === 'DRAFT' ? { ...m, ref: version } : m)),
    );
  const pendingDrafts = draftMembers.filter((bid) =>
    f.state.members.some((m) => m.blueprintId === bid && m.ref === 'DRAFT'),
  );
  const latestOf = (bid: string): number | null =>
    byId.get(bid)?.latestVersion ?? detail.members.find((m) => m.blueprintId === bid)?.newerVersion ?? null;

  const testDoc = set.testRunId ? (
    testRun.data?.expired ? (
      <StatusIndicator type="stopped">The test document has expired</StatusIndicator>
    ) : (
      <Button disabled={!testRun.data || saving} onClick={() => void runOnTestDocument()}>
        Run on test document
      </Button>
    )
  ) : null;

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        description={set.latestVersion ? `Latest version: ${set.latestVersion}` : 'Not published'}
        actions={
          <SpaceBetween direction="horizontal" size="xs" alignItems="center">
            {testDoc}
            <Button onClick={() => navigate(`/run?setId=${encodeURIComponent(id)}`)}>Run a document</Button>
            <Button
              disabled={f.dirty || saving}
              disabledReason={f.dirty ? 'Save your changes before publishing.' : undefined}
              onClick={() => setPublishOpen(true)}
            >
              Publish
            </Button>
            <Button variant="primary" loading={saving} disabled={!f.dirty} onClick={() => void save()}>
              Save
            </Button>
          </SpaceBetween>
        }
      >
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          <span>{set.name}</span>
          <Badge color="blue">Draft set</Badge>
          {set.archived && <Badge color="grey">Archived</Badge>}
        </SpaceBetween>
      </Header>

      {conflict !== null && (
        <Alert
          type="warning"
          header="This set changed in another tab."
          action={<Button onClick={() => void save(conflict)}>Overwrite</Button>}
        >
          Overwrite saves your changes over the other ones. Reload the page to see the other changes instead.
        </Alert>
      )}

      {pendingDrafts.length > 0 && (
        <Alert type="error" header="Some blueprints are used at their draft">
          <SpaceBetween size="s">
            <Box>
              A set version uses published blueprint versions only. Publish each blueprint first, or pin it to one of
              its versions.
            </Box>
            {pendingDrafts.map((bid) => {
              const name = nameOf(bid);
              const latest = latestOf(bid);
              const options = Array.from({ length: latest ?? 0 }, (_, i) => latest! - i).map((v) => ({
                value: String(v),
                label: `Version ${v}`,
              }));
              return (
                <SpaceBetween key={bid} direction="horizontal" size="s" alignItems="center">
                  <Box fontWeight="bold">{name}</Box>
                  <Link
                    href={`/blueprints/${encodeURIComponent(bid)}/edit`}
                    external
                    externalIconAriaLabel="(opens in a new tab)"
                  >
                    {`Publish ${name}`}
                  </Link>
                  <Select
                    ariaLabel={`Pin ${name} to version`}
                    placeholder="Pin to version"
                    selectedOption={null}
                    options={options}
                    disabled={options.length === 0}
                    empty="No published versions"
                    onChange={(e) => pinMember(bid, Number(e.detail.selectedOption.value))}
                  />
                </SpaceBetween>
              );
            })}
          </SpaceBetween>
        </Alert>
      )}

      <DetailsFields
        name={f.state.name}
        description={f.state.description}
        onName={(v) => f.update('name', v)}
        onDescription={(v) => f.update('description', v)}
        nameError={f.nameError}
        descriptionError={f.descriptionError}
      />

      <SpaceBetween size="s">
        <SetMembersEditor
          value={f.state.members}
          onChange={(v) => f.update('members', v)}
          resolved={detail.members}
          fieldErrors={f.memberApiErrors}
          forPublish={forPublish}
          onValidationChange={f.setMemberValidation}
        />
        <Container>
          <CoverageFloorInput
            value={f.state.floorText}
            onChange={(v) => f.update('floorText', v)}
            errorText={f.floorError}
          />
        </Container>
      </SpaceBetween>

      <Container
        header={
          <Header variant="h2" description="Header fields, rollups, and checks across the documents of a packet.">
            Packet summary rules
          </Header>
        }
      >
        <SummaryRulesEditor
          value={f.state.summaryRules}
          members={rulesMembers}
          onChange={(v) => f.update('summaryRules', v)}
          fieldErrors={f.rulesApiErrors}
        />
      </Container>

      <Table<SetDetail['versions'][number]>
        header={
          <Header
            variant="h2"
            counter={`(${detail.versions.length})`}
            description="Select two versions to compare them."
            actions={
              <Button
                disabled={selectedVersions.length !== 2}
                disabledReason={selectedVersions.length !== 2 ? 'Select two versions.' : undefined}
                onClick={() => {
                  const [x, y] = selectedVersions.map((v) => v.version).sort((p, q) => p - q);
                  setCompare([x!, y!]);
                }}
              >
                Compare
              </Button>
            }
          >
            Versions
          </Header>
        }
        items={detail.versions}
        trackBy="version"
        selectionType="multi"
        selectedItems={selectedVersions}
        onSelectionChange={(e) => setSelectedVersions(e.detail.selectedItems.slice(-2))}
        ariaLabels={{
          selectionGroupLabel: 'Versions to compare',
          allItemsSelectionLabel: () => 'Select all versions',
          itemSelectionLabel: (_, v) => `Select version ${v.version}`,
        }}
        empty={
          <Box textAlign="center" color="inherit">
            Not published yet.
          </Box>
        }
        columnDefinitions={[
          {
            id: 'version',
            header: 'Version',
            cell: (v) => (
              <RouterLink
                to={`/sets/${id}/versions/${v.version}`}
                ariaLabel={`View version ${v.version}`}
              >{`Version ${v.version}`}</RouterLink>
            ),
            isRowHeader: true,
          },
          { id: 'published', header: 'Published', cell: (v) => formatDate(v.publishedAt) },
          { id: 'by', header: 'Published by', cell: (v) => v.publishedBy },
          { id: 'members', header: 'Members', cell: (v) => v.memberCount },
          { id: 'floor', header: COVERAGE_FLOOR_LABEL, cell: (v) => `${shareToPercent(v.coverageFloor)} %` },
          { id: 'note', header: 'Change note', cell: (v) => v.changeNote || '-' },
        ]}
      />
      {compare && <SetVersionDiff setId={id} a={compare[0]} b={compare[1]} />}

      <SetRunsTable setId={id} />

      <Modal
        visible={publishOpen}
        onDismiss={() => setPublishOpen(false)}
        header="Publish set"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setPublishOpen(false)}>
                Cancel
              </Button>
              <Button variant="primary" loading={publishing} onClick={() => void publish()}>
                Publish
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="s">
          <Box>
            Publishing creates version {(set.latestVersion ?? 0) + 1}, which does not change when its blueprints change.
          </Box>
          <FormField
            label={
              <span>
                Change note <i>- optional</i>
              </span>
            }
            controlId="set-change-note"
            errorText={changeNote.length > 500 ? 'Keep the change note to 500 characters.' : undefined}
          >
            <Textarea controlId="set-change-note" value={changeNote} onChange={(e) => setChangeNote(e.detail.value)} />
          </FormField>
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  );
}
